import { z } from 'zod';

import type {
  EditionContentV1,
  EditionDraftV1,
  EditionReleaseV1,
  MapFeature,
} from './schemas/edition.js';
import type { Position } from './schemas/common.js';
import {
  EditionContentV1Schema,
  EditionDraftV1Schema,
  EditionReleaseV1Schema,
} from './schemas/edition.js';

export const ValidationSeveritySchema = z.enum(['error', 'warning']);

export const ValidationIssueSchema = z
  .object({
    code: z.string().min(1),
    severity: ValidationSeveritySchema,
    message: z.string().min(1),
    path: z.array(z.union([z.string(), z.number().int()])),
    objectId: z.string().min(1).optional(),
  })
  .strict();

export type ValidationSeverity = z.infer<typeof ValidationSeveritySchema>;
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

export type ValidationResult<T> = {
  valid: boolean;
  issues: ValidationIssue[];
  data?: T;
};

export type EditionValidationOptions = {
  forPublication?: boolean;
};

type ValidationPath = Array<string | number>;

function makeIssue(
  code: string,
  severity: ValidationSeverity,
  message: string,
  path: ValidationPath,
  objectId?: string,
): ValidationIssue {
  const issue = { code, severity, message, path };
  return objectId === undefined ? issue : { ...issue, objectId };
}

function zodIssues(error: z.ZodError): ValidationIssue[] {
  return error.issues.map((issue) =>
    makeIssue(
      `schema.${issue.code}`,
      'error',
      issue.message,
      issue.path.map((part) => (typeof part === 'symbol' ? String(part) : part)),
    ),
  );
}

function addDuplicateIdIssues<T>(
  items: readonly T[],
  getId: (item: T) => string,
  pathPrefix: ValidationPath,
  label: string,
  issues: ValidationIssue[],
): void {
  const firstIndexById = new Map<string, number>();

  items.forEach((item, index) => {
    const id = getId(item);
    const firstIndex = firstIndexById.get(id);
    if (firstIndex === undefined) {
      firstIndexById.set(id, index);
      return;
    }

    issues.push(
      makeIssue(
        'duplicate_id',
        'error',
        `${label} ID "${id}" is duplicated (first used at index ${firstIndex}).`,
        [...pathPrefix, index, 'id'],
        id,
      ),
    );
  });
}

function positionsEqual(left: Position, right: Position, tolerance = 0.000_01): boolean {
  return Math.abs(left[0] - right[0]) <= tolerance && Math.abs(left[1] - right[1]) <= tolerance;
}

function ringIsClosed(ring: readonly Position[]): boolean {
  const first = ring[0];
  const last = ring.at(-1);
  return first !== undefined && last !== undefined && positionsEqual(first, last, 0);
}

function signedArea(points: readonly Position[]): number {
  let area = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    if (current !== undefined && next !== undefined) {
      area += current[0] * next[1] - next[0] * current[1];
    }
  }
  return area / 2;
}

function orientation(a: Position, b: Position, c: Position): number {
  return (b[1] - a[1]) * (c[0] - b[0]) - (b[0] - a[0]) * (c[1] - b[1]);
}

function segmentsCross(a: Position, b: Position, c: Position, d: Position): boolean {
  const first = orientation(a, b, c);
  const second = orientation(a, b, d);
  const third = orientation(c, d, a);
  const fourth = orientation(c, d, b);
  return first * second < 0 && third * fourth < 0;
}

function validateEditionTimes(content: EditionContentV1, issues: ValidationIssue[]): void {
  const editionStart = Date.parse(content.edition.startsAt);
  const editionEnd = Date.parse(content.edition.endsAt);

  try {
    new Intl.DateTimeFormat('en', { timeZone: content.edition.timezone }).format(editionStart);
  } catch {
    issues.push(
      makeIssue(
        'edition.invalid_timezone',
        'error',
        `"${content.edition.timezone}" is not a supported IANA time zone.`,
        ['edition', 'timezone'],
        content.edition.id,
      ),
    );
  }

  if (editionEnd <= editionStart) {
    issues.push(
      makeIssue(
        'edition.invalid_time_range',
        'error',
        'Edition end must be later than its start.',
        ['edition', 'endsAt'],
        content.edition.id,
      ),
    );
  }

  if (content.edition.arrivalStartsAt !== undefined) {
    const arrivalStart = Date.parse(content.edition.arrivalStartsAt);
    if (arrivalStart >= editionStart) {
      issues.push(
        makeIssue(
          'edition.invalid_arrival_time',
          'error',
          'Arrival must start before the live edition starts.',
          ['edition', 'arrivalStartsAt'],
          content.edition.id,
        ),
      );
    }
  }

  content.timetable.forEach((act, index) => {
    const startsAt = Date.parse(act.startsAt);
    const endsAt = Date.parse(act.endsAt);
    if (endsAt <= startsAt) {
      issues.push(
        makeIssue(
          'timetable.invalid_time_range',
          'error',
          'Act end must be later than its start.',
          ['timetable', index, 'endsAt'],
          act.id,
        ),
      );
    }
    if (startsAt < editionStart || endsAt > editionEnd) {
      issues.push(
        makeIssue(
          'timetable.outside_edition',
          'error',
          'Act must be completely inside the edition time range.',
          ['timetable', index, 'startsAt'],
          act.id,
        ),
      );
    }
  });

  const firstActBySlot = new Map<string, string>();
  content.timetable.forEach((act, index) => {
    const slot = [act.title.de.toLocaleLowerCase('de'), act.stageId, act.startsAt, act.endsAt].join('|');
    const firstActId = firstActBySlot.get(slot);
    if (firstActId === undefined) {
      firstActBySlot.set(slot, act.id);
      return;
    }
    issues.push(
      makeIssue(
        'timetable.duplicate_slot',
        'error',
        `Act duplicates the complete timetable slot of "${firstActId}".`,
        ['timetable', index],
        act.id,
      ),
    );
  });
}

function validateStageReferences(
  content: EditionContentV1,
  forPublication: boolean,
  issues: ValidationIssue[],
): void {
  const stageIds = new Set(content.stages.map((stage) => stage.id));
  const featureById = new Map(content.map.features.features.map((feature) => [feature.id, feature]));

  content.timetable.forEach((act, index) => {
    if (!stageIds.has(act.stageId)) {
      issues.push(
        makeIssue(
          'timetable.unknown_stage',
          'error',
          `Act references unknown stage "${act.stageId}".`,
          ['timetable', index, 'stageId'],
          act.id,
        ),
      );
    }
  });

  content.stages.forEach((stage, index) => {
    if (stage.mapFeatureId === undefined) {
      issues.push(
        makeIssue(
          'stage.missing_map_feature',
          forPublication ? 'error' : 'warning',
          'Every published timetable stage must be linked to a stage on the map.',
          ['stages', index, 'mapFeatureId'],
          stage.id,
        ),
      );
      return;
    }
    const feature = featureById.get(stage.mapFeatureId);
    if (feature === undefined || feature.properties.kind !== 'stage') {
      issues.push(
        makeIssue(
          'stage.invalid_map_feature',
          'error',
          `Stage references missing or non-stage map feature "${stage.mapFeatureId}".`,
          ['stages', index, 'mapFeatureId'],
          stage.id,
        ),
      );
    } else if (feature.properties.stageId !== stage.id) {
      issues.push(
        makeIssue(
          'stage.map_feature_mismatch',
          'error',
          'Stage and map feature do not reference each other.',
          ['stages', index, 'mapFeatureId'],
          stage.id,
        ),
      );
    }
  });
}

function expectedGeometryType(feature: MapFeature): 'Point' | 'LineString' | 'Polygon' {
  switch (feature.properties.kind) {
    case 'path':
      return 'LineString';
    case 'area':
      return 'Polygon';
    case 'poi':
    case 'stage':
    case 'landmark':
      return 'Point';
  }
}

function validateMapFeatures(
  content: EditionContentV1,
  forPublication: boolean,
  issues: ValidationIssue[],
): void {
  const categories = content.map.categories;
  const features = content.map.features.features;
  const categoryIds = new Set(categories.map((category) => category.id));
  const stageIds = new Set(content.stages.map((stage) => stage.id));

  features.forEach((feature, index) => {
    if (feature.id !== feature.properties.id) {
      issues.push(
        makeIssue(
          'map.feature_id_mismatch',
          'error',
          'GeoJSON feature ID and properties ID must match.',
          ['map', 'features', 'features', index, 'properties', 'id'],
          feature.id,
        ),
      );
    }

    const expected = expectedGeometryType(feature);
    if (feature.geometry.type !== expected) {
      issues.push(
        makeIssue(
          'map.invalid_geometry_kind',
          'error',
          `${feature.properties.kind} features require ${expected} geometry.`,
          ['map', 'features', 'features', index, 'geometry', 'type'],
          feature.id,
        ),
      );
    }

    if (feature.properties.categoryId !== undefined && !categoryIds.has(feature.properties.categoryId)) {
      issues.push(
        makeIssue(
          'map.unknown_category',
          'error',
          `Feature references unknown category "${feature.properties.categoryId}".`,
          ['map', 'features', 'features', index, 'properties', 'categoryId'],
          feature.id,
        ),
      );
    }

    if (
      (feature.properties.kind === 'poi' || feature.properties.kind === 'landmark') &&
      feature.properties.categoryId === undefined
    ) {
      issues.push(
        makeIssue(
          'map.missing_category',
          forPublication ? 'error' : 'warning',
          'POIs and landmarks require a category before publication.',
          ['map', 'features', 'features', index, 'properties', 'categoryId'],
          feature.id,
        ),
      );
    }

    if (feature.properties.kind === 'stage') {
      if (feature.properties.stageId === undefined || !stageIds.has(feature.properties.stageId)) {
        issues.push(
          makeIssue(
            'map.unknown_stage',
            'error',
            'Stage feature must reference an existing timetable stage.',
            ['map', 'features', 'features', index, 'properties', 'stageId'],
            feature.id,
          ),
        );
      }
    } else if (feature.properties.stageId !== undefined) {
      issues.push(
        makeIssue(
          'map.unexpected_stage_reference',
          'error',
          'Only stage features may carry a stage reference.',
          ['map', 'features', 'features', index, 'properties', 'stageId'],
          feature.id,
        ),
      );
    }

    if (feature.geometry.type === 'Polygon') {
      feature.geometry.coordinates.forEach((ring, ringIndex) => {
        if (!ringIsClosed(ring)) {
          issues.push(
            makeIssue(
              'map.open_polygon_ring',
              'error',
              'Polygon rings must end at their first coordinate.',
              ['map', 'features', 'features', index, 'geometry', 'coordinates', ringIndex],
              feature.id,
            ),
          );
        }
      });
    }
  });
}

function validateRouting(content: EditionContentV1, issues: ValidationIssue[]): void {
  const graph = content.map.routing;
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const featureIds = new Set(content.map.features.features.map((feature) => feature.id));
  const adjacency = new Map(graph.nodes.map((node) => [node.id, new Set<string>()]));

  graph.nodes.forEach((node, index) => {
    if (node.mapFeatureId !== undefined && !featureIds.has(node.mapFeatureId)) {
      issues.push(
        makeIssue(
          'routing.unknown_map_feature',
          'error',
          `Routing node references unknown map feature "${node.mapFeatureId}".`,
          ['map', 'routing', 'nodes', index, 'mapFeatureId'],
          node.id,
        ),
      );
    }
  });

  graph.edges.forEach((edge, index) => {
    const from = nodeById.get(edge.fromNodeId);
    const to = nodeById.get(edge.toNodeId);
    if (from === undefined) {
      issues.push(
        makeIssue(
          'routing.unknown_node',
          'error',
          `Edge references unknown start node "${edge.fromNodeId}".`,
          ['map', 'routing', 'edges', index, 'fromNodeId'],
          edge.id,
        ),
      );
    }
    if (to === undefined) {
      issues.push(
        makeIssue(
          'routing.unknown_node',
          'error',
          `Edge references unknown end node "${edge.toNodeId}".`,
          ['map', 'routing', 'edges', index, 'toNodeId'],
          edge.id,
        ),
      );
    }
    if (edge.fromNodeId === edge.toNodeId) {
      issues.push(
        makeIssue(
          'routing.self_loop',
          'error',
          'A routing edge must connect two different nodes.',
          ['map', 'routing', 'edges', index, 'toNodeId'],
          edge.id,
        ),
      );
    }

    const first = edge.geometry.coordinates[0];
    const last = edge.geometry.coordinates.at(-1);
    if (from !== undefined && first !== undefined && !positionsEqual(from.position, first)) {
      issues.push(
        makeIssue(
          'routing.geometry_start_mismatch',
          'error',
          'Edge geometry must start at its start node.',
          ['map', 'routing', 'edges', index, 'geometry', 'coordinates', 0],
          edge.id,
        ),
      );
    }
    if (to !== undefined && last !== undefined && !positionsEqual(to.position, last)) {
      issues.push(
        makeIssue(
          'routing.geometry_end_mismatch',
          'error',
          'Edge geometry must end at its end node.',
          ['map', 'routing', 'edges', index, 'geometry', 'coordinates', edge.geometry.coordinates.length - 1],
          edge.id,
        ),
      );
    }

    if (from !== undefined && to !== undefined && edge.fromNodeId !== edge.toNodeId) {
      adjacency.get(from.id)?.add(to.id);
      adjacency.get(to.id)?.add(from.id);
    }
  });

  const firstNode = graph.nodes[0];
  if (firstNode !== undefined) {
    const reached = new Set<string>([firstNode.id]);
    const queue = [firstNode.id];
    while (queue.length > 0) {
      const current = queue.shift();
      if (current === undefined) {
        break;
      }
      for (const neighbour of adjacency.get(current) ?? []) {
        if (!reached.has(neighbour)) {
          reached.add(neighbour);
          queue.push(neighbour);
        }
      }
    }

    const disconnected = graph.nodes.filter((node) => !reached.has(node.id));
    if (disconnected.length > 0) {
      issues.push(
        makeIssue(
          'routing.disconnected_graph',
          'error',
          `Routing graph is disconnected; unreachable nodes: ${disconnected.map((node) => node.id).join(', ')}.`,
          ['map', 'routing', 'nodes'],
        ),
      );
    }
  }

  const hasPathFeatures = content.map.features.features.some((feature) => feature.properties.kind === 'path');
  if (hasPathFeatures && graph.edges.length === 0) {
    issues.push(
      makeIssue(
        'routing.paths_without_graph',
        'error',
        'Drawn paths must produce a non-empty routing graph.',
        ['map', 'routing'],
      ),
    );
  }
}

function validateOverlay(
  content: EditionContentV1,
  forPublication: boolean,
  issues: ValidationIssue[],
): void {
  const overlay = content.map.overlay;
  if (overlay === undefined) {
    return;
  }

  const uniqueCorners = new Set(overlay.corners.map((corner) => `${corner[0]},${corner[1]}`));
  if (uniqueCorners.size !== 4 || Math.abs(signedArea(overlay.corners)) < 1e-12) {
    issues.push(
      makeIssue(
        'overlay.degenerate_corners',
        'error',
        'Overlay corners must describe a non-zero quadrilateral with four unique points.',
        ['map', 'overlay', 'corners'],
        overlay.assetId,
      ),
    );
  }

  const [topLeft, topRight, bottomRight, bottomLeft] = overlay.corners;
  if (
    segmentsCross(topLeft, topRight, bottomRight, bottomLeft) ||
    segmentsCross(topRight, bottomRight, bottomLeft, topLeft)
  ) {
    issues.push(
      makeIssue(
        'overlay.self_intersection',
        'error',
        'Overlay corner order must be top-left, top-right, bottom-right, bottom-left without crossing.',
        ['map', 'overlay', 'corners'],
        overlay.assetId,
      ),
    );
  }

  if (!overlay.rightsConfirmed) {
    issues.push(
      makeIssue(
        'overlay.rights_unconfirmed',
        forPublication ? 'error' : 'warning',
        'Publishing an overlay requires documented usage rights.',
        ['map', 'overlay', 'rightsConfirmed'],
        overlay.assetId,
      ),
    );
  }
}

function validateInitialView(content: EditionContentV1, issues: ValidationIssue[]): void {
  const view = content.map.initialView;
  if (view?.minZoom !== undefined && view.maxZoom !== undefined && view.minZoom > view.maxZoom) {
    issues.push(
      makeIssue(
        'map.invalid_zoom_range',
        'error',
        'Minimum zoom must not exceed maximum zoom.',
        ['map', 'initialView', 'minZoom'],
      ),
    );
  }
}

function semanticIssues(content: EditionContentV1, forPublication: boolean): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  addDuplicateIdIssues(content.stages, (stage) => stage.id, ['stages'], 'Stage', issues);
  addDuplicateIdIssues(content.timetable, (act) => act.id, ['timetable'], 'Act', issues);
  addDuplicateIdIssues(content.map.categories, (category) => category.id, ['map', 'categories'], 'Category', issues);
  addDuplicateIdIssues(
    content.map.features.features,
    (feature) => feature.id,
    ['map', 'features', 'features'],
    'Map feature',
    issues,
  );
  addDuplicateIdIssues(content.map.routing.nodes, (node) => node.id, ['map', 'routing', 'nodes'], 'Routing node', issues);
  addDuplicateIdIssues(content.map.routing.edges, (edge) => edge.id, ['map', 'routing', 'edges'], 'Routing edge', issues);

  validateEditionTimes(content, issues);
  validateStageReferences(content, forPublication, issues);
  validateMapFeatures(content, forPublication, issues);
  validateRouting(content, issues);
  validateOverlay(content, forPublication, issues);
  validateInitialView(content, issues);

  return issues;
}

function resultWithData<T>(data: T, issues: ValidationIssue[]): ValidationResult<T> {
  return {
    valid: !issues.some((issue) => issue.severity === 'error'),
    issues,
    data,
  };
}

export function validateEditionContent(
  input: unknown,
  options: EditionValidationOptions = {},
): ValidationResult<EditionContentV1> {
  const parsed = EditionContentV1Schema.safeParse(input);
  if (!parsed.success) {
    return { valid: false, issues: zodIssues(parsed.error) };
  }
  return resultWithData(parsed.data, semanticIssues(parsed.data, options.forPublication ?? false));
}

export function validateEditionDraft(input: unknown): ValidationResult<EditionDraftV1> {
  const parsed = EditionDraftV1Schema.safeParse(input);
  if (!parsed.success) {
    return { valid: false, issues: zodIssues(parsed.error) };
  }
  return resultWithData(parsed.data, semanticIssues(parsed.data, false));
}

export function validateEditionRelease(input: unknown): ValidationResult<EditionReleaseV1> {
  const parsed = EditionReleaseV1Schema.safeParse(input);
  if (!parsed.success) {
    return { valid: false, issues: zodIssues(parsed.error) };
  }
  return resultWithData(parsed.data, semanticIssues(parsed.data, true));
}
